import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiError, imgSrc } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProductForm } from "@/components/admin/ProductForm";
import { PlanForm } from "@/components/admin/PlanForm";
import { OrdersList } from "@/components/admin/OrdersList";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  return (
    <div className="grid min-h-screen place-items-center bg-[#07080f] px-5 text-white">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm space-y-4 rounded-2xl border border-white/10 bg-[#0e1120] p-8">
        <img src="/brand/speedmax-icon.png" alt="" className="h-10 w-10" />
        <h1 className="font-display text-2xl font-bold">Painel administrativo</h1>
        <Input data-testid="admin-email-input" type="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-black/30 border-white/15" />
        <Input data-testid="admin-password-input" type="password" placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-black/30 border-white/15" />
        {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
        <Button data-testid="admin-login-button" type="submit" disabled={loading} className="w-full grad-bg text-white">{loading ? "Entrando..." : "Entrar"}</Button>
        <a href="/" className="block text-center text-xs text-zinc-400 hover:text-white">Voltar ao site</a>
      </form>
    </div>
  );
};

const Row = ({ p, onEdit, onDelete }) => (
  <li data-testid={`admin-product-row-${p.id}`} className="flex items-center gap-4 rounded-xl border border-white/10 bg-[#0e1120] p-3">
    <img src={imgSrc(p.image)} alt="" className="h-16 w-16 rounded-lg object-cover bg-black/40" />
    <div className="min-w-0 flex-1">
      <p className="truncate font-semibold">{p.name} {!p.active && <span className="ml-2 text-xs text-amber-400">(oculto)</span>}</p>
      <p className="text-sm text-zinc-400">{p.price ? `R$ ${p.price}` : "Consulte"} · {p.category || "—"}</p>
    </div>
    <Button data-testid={`admin-edit-${p.id}`} size="icon" variant="ghost" onClick={() => onEdit(p)}><Pencil className="h-4 w-4" /></Button>
    <Button data-testid={`admin-delete-${p.id}`} size="icon" variant="ghost" onClick={() => onDelete(p)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
  </li>
);

const Dashboard = ({ user, onLogout }) => {
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [editPlan, setEditPlan] = useState(null);
  const [store, setStore] = useState("plans");
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/products");
      setItems(Array.isArray(data) ? data : []);
      const pr = await api.get("/plans");
      setPlans(Array.isArray(pr.data) ? pr.data : []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.name}"?`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Produto excluído"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const list = items.filter((p) => p.store === store);

  return (
    <div className="min-h-screen bg-[#07080f] text-white">
      <header className="flex items-center justify-between border-b border-white/10 px-5 sm:px-10 py-4">
        <div className="flex items-center gap-3"><img src="/brand/speedmax-icon.png" alt="" className="h-8 w-8" /><span className="font-display font-bold">Painel</span></div>
        <div className="flex items-center gap-3 text-sm text-zinc-400">
          <span className="hidden sm:inline">{user.email}</span>
          <Button data-testid="admin-logout-button" variant="ghost" size="sm" onClick={onLogout}><LogOut className="h-4 w-4 mr-1" />Sair</Button>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-5 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Tabs value={store} onValueChange={setStore}>
            <TabsList className="bg-white/5 h-auto flex-wrap">
              <TabsTrigger data-testid="admin-tab-plans" value="plans">Planos</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-speedmax" value="speedmax">Speed Max · Produtos</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-idealize" value="idealize">Idealize · Canecas</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-orders" value="orders">Pedidos</TabsTrigger>
            </TabsList>
          </Tabs>
          {!["plans", "orders"].includes(store) && <Button data-testid="admin-add-product-button" onClick={() => setEditing({ store })} className="grad-bg text-white"><Plus className="h-4 w-4 mr-1" />Novo item</Button>}
        </div>
        {store === "orders" ? <OrdersList /> : store === "plans" ? (
          <ul className="mt-8 space-y-3">
            {plans.map((p) => (
              <li key={p.pid} data-testid={`admin-plan-row-${p.pid}`} className="flex items-center gap-4 rounded-xl border border-white/10 bg-[#0e1120] p-4">
                <span className="font-display text-2xl font-black outline-num w-12">0{p.pid}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">Opção {p.pid} · R$ {p.price}</p>
                  <p className="truncate text-sm text-zinc-400">{p.link ? "Loja externa" : "WhatsApp"} · {p.tag}</p>
                </div>
                <Button data-testid={`admin-plan-edit-${p.pid}`} size="icon" variant="ghost" onClick={() => setEditPlan(p)}><Pencil className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
        ) : (
        <ul className="mt-8 space-y-3">
          {list.map((p) => <Row key={p.id} p={p} onEdit={setEditing} onDelete={remove} />)}
          {list.length === 0 && <p className="text-zinc-400">Nenhum item cadastrado.</p>}
        </ul>
        )}
      </main>
      {editPlan && <PlanForm plan={editPlan} onClose={() => setEditPlan(null)} onSaved={() => { setEditPlan(null); load(); }} />}
      {editing && <ProductForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); setUser(false); };
  if (user === null) return <div className="min-h-screen bg-[#07080f]" />;
  if (!user) return <Login onLogin={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
}
